// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Every publicly co-funded tower, placed on a computed spatial axis: how far
// away the nearest COMPETING network's tower is. A funded tower 40 km from any
// rival filled a real gap. One built 200 m from a rival's tower added a second
// network where there was already one — which may still be worth doing, but it
// is not what "black spot" means.
//
// This was first drawn as a per-tower beeswarm. With 2,161 towers piled around
// the mode, the swarm had to shrink its dots to a hairline to stay
// non-overlapping and rendered as a smear. A histogram shows the distribution
// honestly; clicking a bar opens the towers inside it, so per-tower detail is
// still one click away.

import type { TowerData, FundedSite } from '../types';
import { CARRIER_ALIAS, CARRIER_COLOR } from '../colors';
import { attr, esc, km, num, pct } from '../format';
import { gloss } from '../glossary';
import { openSite } from '../drawer';
import { legend, statRow, viewHead, onActivate, setPressed } from './ui';
import { logBins, logScale } from '../layout';

// Under 50 m from a rival's tower means the two are on the same structure, or
// on structures a few paces apart. That is a categorically different thing from
// "3 km away", not a smaller number of the same thing, so it gets its own bar
// outside the log axis instead of being crushed into the first bin.
const MIN_KM = 0.05;
const MAX_KM = 120;
const BINS = 30;

export function mount(el: HTMLElement, data: TowerData): void {
  // `measured` drives the distribution; the DENOMINATOR the reader sees is the
  // full co-funded total, with the unmeasurable handful stated rather than
  // silently dropped. Counts come from the pipeline so this view and the
  // front-page card cannot disagree.
  const sum = data.additionality.summary;
  const all = data.additionality.sites.filter((s) => s.km !== null);
  let program: number | null = null;
  let carrier: number | null = null;
  let openBin: number | null = null;

  el.innerHTML = `
    ${viewHead({
      title: 'Did the money fill a gap?',
      question: 'For each tower the public paid for, was a competing network already nearby?',
      blurb: `Each bar counts ${gloss('cofunded', 'co-funded')} towers by how far they sit from the nearest tower belonging to a <em>different</em> carrier. Far right is ${gloss('additionality', 'genuine additionality')} — country no other network reached. Far left means a rival was already there. Click any bar to see the towers inside it.`,
    })}
    ${statRow([
      { label: 'Co-funded towers', value: num(sum.total), sub: `${pct(sum.total / data.meta.totals.sites, 1)} of the network` },
      { label: 'On a rival’s own structure', value: num(sum.sameStructure), sub: `${pct(sum.sameStructure / sum.measured)} of the ${num(sum.measured)} measured — under 50 m apart`, tone: 'warn' },
      { label: '20 km or more from any rival', value: num(sum.beyond20), sub: pct(sum.beyond20 / sum.measured), tone: 'good' },
    ])}
    <div class="ctl-row">
      <div class="seg" role="group" aria-label="Carrier filter">
        <button data-c="" class="active" aria-pressed="true">All carriers</button>
        ${data.meta.carriers.map((c, i) => `<button data-c="${i}" aria-pressed="false">${esc(CARRIER_ALIAS[c])}</button>`).join('')}
      </div>
      <select data-prog aria-label="Funding program">
        <option value="">Every funding program</option>
        ${data.additionality.programs.map((p, i) => `<option value="${i}">${esc(p)}</option>`).join('')}
      </select>
    </div>
    <div data-hist></div>
    ${legend(data.meta.carriers.map((c) => ({ color: CARRIER_COLOR[c], label: CARRIER_ALIAS[c] })))}
    <div data-bin></div>
    <p class="src-note">Distance is straight-line to the nearest ${data.meta.latestYear} site of another carrier. It measures whether a <em>different</em> network was already close, not whether coverage existed — a rival tower 3 km away may still not have reached the specific valley this tower serves. Bars are stacked by the carrier that owns the funded tower, and the last bar is open-ended — the furthest co-funded tower sits ${km(data.additionality.summary.maxKm)} from any rival.${data.additionality.summary.noRival ? ` ${num(data.additionality.summary.noRival)} towers on external territories have no competing network anywhere and are not plotted.` : ''}</p>
  `;

  const host = el.querySelector('[data-hist]') as HTMLElement;
  const binHost = el.querySelector('[data-bin]') as HTMLElement;

  function rows(): FundedSite[] {
    return all.filter(
      (s) => (program === null || s.program === program) && (carrier === null || s.mno === carrier),
    );
  }

  function render(): void {
    const list = rows();
    host.innerHTML = list.length ? histogram(data, list, openBin) : '<div class="empty">No co-funded towers match this filter.</div>';
    host.querySelectorAll('[data-bin-idx]').forEach((b) => {
      onActivate(b, () => {
        const i = Number(b.getAttribute('data-bin-idx'));
        openBin = openBin === i ? null : i;
        render();
      });
    });
    renderBin(list);
  }

  function renderBin(list: FundedSite[]): void {
    if (openBin === null) {
      binHost.innerHTML = '';
      return;
    }
    let items: FundedSite[];
    let heading: string;
    let blurb: string;
    if (openBin === -1) {
      items = list.filter((s) => s.same).sort((a, b) => a.km! - b.km!);
      heading = `${num(items.length)} co-funded tower${items.length === 1 ? '' : 's'} within 50 m of a rival's tower`;
      blurb = 'These are effectively the same structure — a competing network is already on it or standing beside it. Sharing a mast is normal and often sensible, but it does mean the public contribution did not put a signal anywhere that had none.';
    } else {
      const rest = list.filter((s) => !s.same);
      const bin = logBins(rest.map((s) => s.km!), MIN_KM, MAX_KM, BINS)[openBin];
      if (!bin || !bin.count) {
        binHost.innerHTML = '';
        return;
      }
      items = bin.items.map((i) => rest[i]).sort((a, b) => a.km! - b.km!);
      const last = openBin === BINS - 1;
      heading = last
        ? `${num(bin.count)} co-funded tower${bin.count === 1 ? '' : 's'} ${km(bin.lo)} or more from the nearest rival`
        : `${num(bin.count)} co-funded tower${bin.count === 1 ? '' : 's'} ${km(bin.lo)}–${km(bin.hi)} from the nearest rival`;
      blurb = bin.hi <= 1
        ? 'A competing network was already within a kilometre of these.'
        : bin.lo >= 20
          ? 'No other network had anything within 20 km of these — the clearest case that the money bought coverage that did not exist.'
          : 'Somewhere in between: a rival was in the district, but not necessarily reaching the same properties.';
    }
    if (!items.length) {
      binHost.innerHTML = '';
      return;
    }
    binHost.innerHTML = `<section class="card">
      <div class="card-head">
        <h2>${esc(heading)}</h2>
        <p>${esc(blurb)} Click any row for the full tower detail.</p>
      </div>
      <div class="table-scroll">
        <table class="data-table">
          <thead><tr><th>Site</th><th>Network</th><th>Region</th><th>Program</th><th class="n">Nearest rival</th></tr></thead>
          <tbody>${items
            .slice(0, 200)
            .map(
              (s) => `<tr data-rfnsa="${attr(s.rfnsa)}" data-mno="${s.mno}" tabindex="0">
                <td>${esc(s.rfnsa || '—')}</td>
                <td><span class="dot" style="background:${CARRIER_COLOR[data.meta.carriers[s.mno]]}"></span>${esc(CARRIER_ALIAS[data.meta.carriers[s.mno]])}</td>
                <td>${s.sa3 >= 0 ? esc(data.regions[s.sa3].name) : '—'}</td>
                <td>${esc(data.additionality.programs[s.program])}${s.round ? ` · r${esc(s.round)}` : ''}</td>
                <td class="n">${km(s.km!)}</td>
              </tr>`,
            )
            .join('')}</tbody>
        </table>
      </div>
      ${items.length > 200 ? `<p class="src-note">Showing the 200 closest of ${num(items.length)} towers in this bin.</p>` : ''}
      <button class="btn btn-quiet" data-close-bin>Close</button>
    </section>`;
    binHost.querySelectorAll('[data-rfnsa]').forEach((r) => {
      const go = () => {
        const i = findSite(data, r.getAttribute('data-rfnsa')!, Number(r.getAttribute('data-mno')));
        if (i >= 0) openSite(data, i);
      };
      r.addEventListener('click', go);
      r.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') go();
      });
    });
    binHost.querySelector('[data-close-bin]')?.addEventListener('click', () => {
      openBin = null;
      render();
    });
  }

  el.querySelectorAll('[data-c]').forEach((b) => {
    b.addEventListener('click', () => {
      const v = b.getAttribute('data-c')!;
      carrier = v === '' ? null : Number(v);
      openBin = null;
      setPressed(el.querySelectorAll('[data-c]'), (o) => o === b);
      render();
    });
  });
  (el.querySelector('[data-prog]') as HTMLSelectElement).addEventListener('change', (e) => {
    const v = (e.target as HTMLSelectElement).value;
    program = v === '' ? null : Number(v);
    openBin = null;
    render();
  });

  render();
}

function findSite(data: TowerData, rfnsa: string, mno: number): number {
  const s = data.sites;
  for (let i = 0; i < s.n; i++) if (s.rfnsa[i] === rfnsa && s.mno[i] === mno) return i;
  return -1;
}

function histogram(data: TowerData, list: FundedSite[], openBin: number | null): string {
  const W = 960;
  const H = 400;
  // The "same structure" bar sits in its own band on the left, separated by a
  // visible gap so nobody reads it as continuous with the log axis.
  const CAT_W = 74;
  const GAP = 34;
  const P = { t: 52, b: 66, l: 56, r: 20 };
  const ih = H - P.t - P.b;
  const axisL = P.l + CAT_W + GAP;
  const iw = W - axisL - P.r;

  const near = list.filter((s) => s.same);
  const rest = list.filter((s) => !s.same);
  const sumMax = data.additionality.summary.maxKm;
  const bins = logBins(rest.map((s) => s.km!), MIN_KM, MAX_KM, BINS);
  // Stack each bar by the carrier that owns the funded tower.
  const stacks = bins.map((b) => {
    const per = data.meta.carriers.map((_, ci) => b.items.filter((i) => rest[i].mno === ci).length);
    return { bin: b, per };
  });
  // The y-axis is scaled to the DISTRIBUTION, not to the same-structure bar.
  // That bar is several times taller than any distance bin, and letting it set
  // the scale flattens the entire distribution into the baseline. It is drawn
  // clipped at the top with an explicit break mark and its true value printed,
  // which is honest and keeps both readable.
  const max = Math.max(1, ...bins.map((b) => b.count));
  const catClipped = near.length > max;

  const x = logScale(MIN_KM, MAX_KM, axisL, W - P.r);
  const y = (v: number) => P.t + ih - (v / max) * ih;
  const barW = iw / BINS;

  const catPer = data.meta.carriers.map((_, ci) => near.filter((s) => s.mno === ci).length);
  // Scale the segments so the stack fills the plot height when clipped, keeping
  // the carrier proportions within the bar truthful.
  const catScale = near.length ? Math.min(1, max / near.length) : 1;
  let catAcc = 0;
  const catSegs = catPer
    .map((n, ci) => {
      if (!n) return '';
      const y0 = y((catAcc + n) * catScale);
      const h = y(catAcc * catScale) - y0;
      catAcc += n;
      return `<rect x="${P.l}" y="${y0.toFixed(1)}" width="${CAT_W}" height="${h.toFixed(1)}" fill="${CARRIER_COLOR[data.meta.carriers[ci]]}" fill-opacity="0.9" />`;
    })
    .join('');
  const catBreak = catClipped
    ? `<path d="M${P.l - 3},${P.t + 9} l${CAT_W + 6},-6 M${P.l - 3},${P.t + 15} l${CAT_W + 6},-6"
         stroke="var(--bg-surface)" stroke-width="4" fill="none" />
       <text x="${P.l + CAT_W / 2}" y="${P.t - 8}" class="cat-total" text-anchor="middle">${num(near.length)}</text>`
    : '';
  const catTip = [
    'Under 50 m from a rival carrier\'s tower',
    'These are effectively the same structure — a competing network is already on it or beside it.',
    `${num(near.length)} co-funded towers (${pct(near.length / Math.max(list.length, 1))})`,
    ...catPer.map((n, ci) => `${CARRIER_ALIAS[data.meta.carriers[ci]]}: ${num(n)}`).filter((_, ci) => catPer[ci] > 0),
    near.length ? 'Click to list them' : '',
  ].filter(Boolean).join('\n');
  const catBar = `<g class="hist-bar${openBin === -1 ? ' hist-bar-open' : ''}"${near.length ? ' data-bin-idx="-1" role="button" tabindex="0"' : ''} data-tip="${attr(catTip)}">
      <rect x="${P.l}" y="${P.t}" width="${CAT_W}" height="${ih}" fill="transparent" />
      ${catSegs}${catBreak}
    </g>
    <text x="${P.l + CAT_W / 2}" y="${H - P.b + 22}" class="ax" text-anchor="middle">same</text>
    <text x="${P.l + CAT_W / 2}" y="${H - P.b + 36}" class="ax" text-anchor="middle">structure</text>
    <line x1="${P.l + CAT_W + GAP / 2}" x2="${P.l + CAT_W + GAP / 2}" y1="${P.t - 6}" y2="${H - P.b + 6}" stroke="var(--border-default)" stroke-dasharray="2 5" />`;

  const bars = stacks
    .map(({ bin, per }, i) => {
      const bx = axisL + i * barW;
      let acc = 0;
      const segs = per
        .map((n, ci) => {
          if (!n) return '';
          const y0 = y(acc + n);
          const h = y(acc) - y0;
          acc += n;
          return `<rect x="${(bx + 0.6).toFixed(1)}" y="${y0.toFixed(1)}" width="${(barW - 1.2).toFixed(1)}" height="${h.toFixed(1)}"
            fill="${CARRIER_COLOR[data.meta.carriers[ci]]}" fill-opacity="0.9" />`;
        })
        .join('');
      const isLast = i === BINS - 1;
      const tip = [
        isLast
          ? `${km(bin.lo)} or more from the nearest rival tower (furthest is ${km(sumMax)})`
          : `${km(bin.lo)} – ${km(bin.hi)} from the nearest rival tower`,
        `${num(bin.count)} co-funded towers (${pct(bin.count / Math.max(list.length, 1))})`,
        ...per.map((n, ci) => `${CARRIER_ALIAS[data.meta.carriers[ci]]}: ${num(n)}`).filter((_, ci) => per[ci] > 0),
        bin.count ? 'Click to list them' : 'No towers in this range',
      ].filter(Boolean).join('\n');
      // A full-height transparent hit area, so a one-tower bar is still clickable.
      // Only a bar with towers in it is announced and wired as a button — an
      // empty bin that looks clickable and opens nothing is a dead control.
      const live = bin.count > 0;
      return `<g class="hist-bar${openBin === i ? ' hist-bar-open' : ''}"${live ? ` data-bin-idx="${i}" role="button" tabindex="0"` : ''} data-tip="${attr(tip)}">
        <rect x="${bx.toFixed(1)}" y="${P.t}" width="${barW.toFixed(1)}" height="${ih}" fill="transparent" />
        ${segs}
      </g>`;
    })
    .join('');

  const marks = [
    { d: 1, label: 'a rival was already here', cls: 'bad' },
    { d: 20, label: 'genuine gap', cls: 'good' },
  ]
    .map(
      (m) =>
        `<line x1="${x(m.d).toFixed(1)}" x2="${x(m.d).toFixed(1)}" y1="${P.t - 18}" y2="${H - P.b}" stroke="var(--text-secondary)" stroke-dasharray="4 4" />
         <text x="${x(m.d).toFixed(1)}" y="${P.t - 24}" class="ax ${m.cls}" text-anchor="middle">${esc(m.label)}</text>`,
    )
    .join('');

  const yTicks = [0, 0.25, 0.5, 0.75, 1]
    .map((f) => Math.round(max * f))
    .filter((v, i, a) => a.indexOf(v) === i)
    .map(
      (v) =>
        `<line x1="${P.l}" x2="${W - P.r}" y1="${y(v).toFixed(1)}" y2="${y(v).toFixed(1)}" stroke="var(--border-subtle)" />
         <text x="${P.l - 8}" y="${(y(v) + 4).toFixed(1)}" class="ax" text-anchor="end">${num(v)}</text>`,
    )
    .join('');

  const xTicks = [0.1, 0.5, 2, 10, 50, 120]
    .map(
      (d) =>
        `<text x="${x(d).toFixed(1)}" y="${H - P.b + 22}" class="ax" text-anchor="middle">${d < 1 ? `${Math.round(d * 1000)} m` : d === MAX_KM ? `${d} km+` : `${d} km`}</text>`,
    )
    .join('');

  return `<div class="chart hist-wrap">
    <svg viewBox="0 0 ${W} ${H}" class="hist" role="img"
      aria-label="Co-funded towers by distance to the nearest competing carrier's tower">
      ${yTicks}${catBar}${marks}${bars}
      <line x1="${P.l}" x2="${W - P.r}" y1="${(H - P.b).toFixed(1)}" y2="${(H - P.b).toFixed(1)}" stroke="var(--border-default)" />
      ${xTicks}
      <text x="${W / 2}" y="${H - 8}" class="ax-title" text-anchor="middle">distance to the nearest tower of a different network →</text>
      <text x="14" y="${P.t + ih / 2}" class="ax-title" text-anchor="middle" transform="rotate(-90 14 ${P.t + ih / 2})">co-funded towers</text>
    </svg>
  </div>`;
}
